<template>
    <div class="buscador-campos">
        <v-row dense align="center">
            <v-col cols="12" :sm="anchoSelector.sm" :md="anchoSelector.md">
                <v-select
                    dense hide-details outlined
                    label="Buscar por"
                    v-model="campoActual"
                    :items="campos"
                    item-text="texto"
                    item-value="valor"
                    prepend-inner-icon="filter_list"
                    @change="cambiarCampo"
                />
            </v-col>

            <v-col cols="12" :sm="anchoCampo.sm" :md="anchoCampo.md">
                <!-- Nombre del participante: sugerencias de personas mientras se escribe -->
                <v-autocomplete
                    v-if="conSugerencias"
                    ref="entrada"
                    dense hide-details outlined clearable
                    :label="etiqueta"
                    :placeholder="placeholder"
                    prepend-inner-icon="search"
                    :items="sugerencias"
                    :loading="cargandoSugerencias"
                    :search-input.sync="texto"
                    item-text="etiqueta"
                    item-value="persona_id"
                    return-object
                    no-filter
                    hide-no-data
                    :menu-props="{maxHeight: 320}"
                    v-model="participante"
                    @change="seleccionarParticipante"
                    @keyup.enter="buscarAhora"
                    @click:clear="limpiarTexto"
                >
                    <template v-slot:item="{ item }">
                        <v-list-item-avatar color="primary" size="32" class="white--text text-subtitle-2">
                            {{ item.iniciales }}
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title>{{ item.nombre }} {{ item.apellido }}</v-list-item-title>
                            <v-list-item-subtitle>Identidad {{ item.numero_identidad }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </template>
                </v-autocomplete>

                <!-- Resto de campos: texto libre -->
                <v-text-field
                    v-else
                    ref="entrada"
                    dense hide-details outlined clearable
                    :label="etiqueta"
                    :placeholder="placeholder"
                    :inputmode="campoActual === 'identidad' || campoActual === 'registro' ? 'numeric' : 'text'"
                    prepend-inner-icon="search"
                    v-model="texto"
                    :loading="cargando"
                    @keyup.enter="buscarAhora"
                    @click:clear="limpiarTexto"
                />
            </v-col>
        </v-row>

        <!-- Filtros activos -->
        <v-row dense v-if="chips.length" class="mt-1">
            <v-col cols="12" class="d-flex flex-wrap align-center">
                <span class="text-caption grey--text text--darken-1 mr-2">Filtrando por:</span>
                <v-chip
                    v-for="chip in chips" :key="chip.clave"
                    small close outlined color="primary" class="mr-1 mb-1"
                    @click:close="quitarChip(chip.clave)">
                    <v-icon left small>{{ chip.icono }}</v-icon>
                    <strong class="mr-1">{{ chip.titulo }}:</strong> {{ chip.valor }}
                </v-chip>
                <v-btn text x-small color="secondary" class="mb-1" @click="limpiarTodo">
                    <v-icon small left>backspace</v-icon> Limpiar todo
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>
/**
 * Buscador con campo explícito ("Buscar por"), búsqueda mientras se escribe,
 * sugerencias de participantes y etiquetas de filtros activos.
 *
 * Emite `buscar` con {texto, campo, persona} cada vez que cambia el criterio
 * (con espera tras la última tecla) y `quitar` con la clave de una etiqueta
 * externa cuando el usuario la cierra. Las etiquetas externas (año, unidad...)
 * se pasan en `chipsExtra`.
 *
 * Uso:
 *   <buscador-campos :campos="campos" :chips-extra="chips" sugerir-personas
 *                    @buscar="onBuscar" @quitar="onQuitar" @limpiar="onLimpiar"/>
 */
const ESPERA_BUSQUEDA = 450;
const MINIMO_LETRAS = 3;

export const CAMPOS_CURSOS = [
    {valor: "todos", texto: "Todos los campos", icono: "manage_search"},
    {valor: "identidad", texto: "Identidad", icono: "badge"},
    {valor: "nombre", texto: "Nombre del participante", icono: "person_search"},
    {valor: "registro", texto: "N.° de registro", icono: "confirmation_number"},
    {valor: "curso", texto: "Curso", icono: "school"},
];

export const CAMPOS_PERSONAS = [
    {valor: "todos", texto: "Identidad o nombre", icono: "manage_search"},
    {valor: "identidad", texto: "Identidad", icono: "badge"},
    {valor: "nombre", texto: "Nombre", icono: "person_search"},
];

export default {
    name: "BuscadorCampos",
    props: {
        campos: {type: Array, default: () => CAMPOS_CURSOS},
        // Muestra sugerencias de personas cuando el campo es "nombre".
        sugerirPersonas: {type: Boolean, default: false},
        // Tipo de persona para las sugerencias (3 = estudiantes, 2 = administradores).
        tipoPersona: {type: Number, default: 3},
        // Indicador de carga del listado que usa el buscador.
        cargando: {type: Boolean, default: false},
        // Etiquetas de otros filtros: [{clave, icono, titulo, valor}]
        chipsExtra: {type: Array, default: () => []},
        anchoSelector: {type: Object, default: () => ({sm: 4, md: 3})},
        anchoCampo: {type: Object, default: () => ({sm: 8, md: 9})},
    },
    data() {
        return {
            campoActual: "todos",
            texto: "",
            participante: null,
            sugerencias: [],
            cargandoSugerencias: false,
            temporizador: null,
            temporizadorSugerencias: null,
            // Último criterio emitido; evita repetir la misma consulta.
            ultimo: {texto: "", campo: "todos", persona: null},
        }
    },
    computed: {
        conSugerencias() {
            return this.sugerirPersonas && this.campoActual === "nombre";
        },
        etiqueta() {
            const etiquetas = {
                todos: "Escribe para buscar",
                identidad: "Número de identidad",
                nombre: "Nombre o apellido",
                registro: "Número de registro",
                curso: "Nombre del curso",
            };
            const campo = this.campos.find(c => c.valor === this.campoActual);
            return etiquetas[this.campoActual] || (campo ? campo.texto : "Buscar");
        },
        placeholder() {
            const ejemplos = {
                todos: "Escribe al menos 3 caracteres",
                identidad: "Ej. 0801199912345, sin guiones",
                nombre: "Ej. Juan Carlos Mondragón",
                registro: "Ej. 2658171",
                curso: "Ej. Excel intermedio",
            };
            return ejemplos[this.campoActual] || "";
        },
        chips() {
            const chips = [];
            if (this.participante) {
                chips.push({
                    clave: "__participante", icono: "person", titulo: "Participante",
                    valor: `${this.participante.nombre} ${this.participante.apellido} (${this.participante.numero_identidad})`,
                });
            } else if (this.ultimo.texto) {
                const campo = this.campos.find(c => c.valor === this.ultimo.campo);
                chips.push({
                    clave: "__texto", icono: campo ? campo.icono : "search", titulo: campo ? campo.texto : "Texto",
                    valor: this.ultimo.texto,
                });
            }
            return chips.concat(this.chipsExtra);
        },
        // Palabras a resaltar en las tablas, normalizadas como en el servidor.
        palabras() {
            if (!this.ultimo.texto || this.participante) return [];
            return normalizar(this.ultimo.texto).split(/\s+/).filter(p => p.length >= 2);
        },
    },
    watch: {
        texto(valor) {
            clearTimeout(this.temporizador);
            clearTimeout(this.temporizadorSugerencias);

            const limpio = (valor || "").trim();

            if (this.conSugerencias && limpio.length >= MINIMO_LETRAS && !this.participante) {
                this.temporizadorSugerencias = setTimeout(() => this.cargarSugerencias(limpio), 300);
            }

            if (limpio.length >= MINIMO_LETRAS || limpio.length === 0) {
                this.temporizador = setTimeout(() => {
                    if (this.participante) return;
                    if (limpio !== this.ultimo.texto) this.emitir();
                }, ESPERA_BUSQUEDA);
            }
        },
    },
    methods: {
        emitir() {
            clearTimeout(this.temporizador);
            const texto = this.participante ? "" : (this.texto || "").trim();
            this.ultimo = {texto, campo: this.campoActual, persona: this.participante ? this.participante.persona_id : null};
            this.$emit("buscar", {...this.ultimo, participante: this.participante});
        },
        buscarAhora() {
            this.emitir();
        },
        cargarSugerencias(texto) {
            this.cargandoSugerencias = true;
            const params = {busqueda: texto, campo: "nombre", cat_tipo_persona_id: this.tipoPersona};
            window.axios.get('/personas/search', {params}).then(response => {
                if (response.data.success) {
                    this.sugerencias = response.data.data.map(p => ({
                        ...p,
                        etiqueta: `${p.nombre} ${p.apellido} · ${p.numero_identidad}`,
                        iniciales: `${(p.nombre || " ")[0]}${(p.apellido || " ")[0]}`.toUpperCase(),
                    }));
                }
            })
                .catch(() => {})
                .finally(() => {
                    this.cargandoSugerencias = false;
                });
        },
        seleccionarParticipante(persona) {
            if (persona) this.texto = "";
            this.emitir();
        },
        cambiarCampo() {
            const habiaCriterio = !!(this.ultimo.texto || this.ultimo.persona);
            this.participante = null;
            this.sugerencias = [];
            this.texto = "";
            this.$nextTick(() => {
                const entrada = this.$refs.entrada;
                if (entrada && entrada.focus) entrada.focus();
            });
            if (habiaCriterio) this.emitir();
        },
        limpiarTexto() {
            this.texto = "";
            this.participante = null;
            this.emitir();
        },
        quitarChip(clave) {
            if (clave === "__participante" || clave === "__texto") {
                this.limpiarTexto();
                return;
            }
            this.$emit("quitar", clave);
        },
        limpiarTodo() {
            this.texto = "";
            this.participante = null;
            this.campoActual = this.campos[0].valor;
            this.ultimo = {texto: "", campo: this.campoActual, persona: null};
            this.$emit("limpiar");
        },
        /**
         * Resalta las palabras buscadas dentro de un valor, para usar con v-html.
         * `columna` es el campo al que pertenece la celda (identidad, nombre, registro, curso).
         */
        resaltar(valor, columna) {
            const texto = escapar(valor);
            const aplica = this.ultimo.campo === "todos" || this.ultimo.campo === columna;
            if (!aplica || !this.palabras.length) return texto;

            const plano = normalizar(texto);
            let resultado = "";
            let i = 0;
            while (i < texto.length) {
                let coincidencia = 0;
                for (const palabra of this.palabras) {
                    if (plano.startsWith(palabra, i) && palabra.length > coincidencia) coincidencia = palabra.length;
                }
                if (coincidencia) {
                    resultado += `<mark class="resaltado">${texto.substr(i, coincidencia)}</mark>`;
                    i += coincidencia;
                } else {
                    resultado += texto[i];
                    i++;
                }
            }
            return resultado;
        },
    },
}

function normalizar(texto) {
    return (texto || "").toString().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

function escapar(texto) {
    return (texto || "").toString().replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
</script>

<style>
mark.resaltado {
    background: #F6E3BF;
    color: #1B2340;
    border-radius: 3px;
    padding: 0 2px;
}
</style>
